import React, { useMemo, useState, useEffect } from 'react';
import {
  PlusIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  VideoIcon,
  PhoneIcon,
  MapPinIcon,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Toast } from '../Toast';
import {
  useClientAppointments,
  useCreateAppointment,
  useUpdateAppointment,
} from '../../hooks/useAppointments';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../lib/api';
import { useUndoableAction } from '../../hooks/useUndoableAction';
import { AppointmentCard } from '../appointments/AppointmentCard';
import { AppointmentFormModal, AppointmentFormData } from '../appointments/AppointmentFormModal';
import { CancelAppointmentModal } from '../appointments/CancelAppointmentModal';
import type { Appointment, AppointmentStatus, MeetingType } from '../../types/appointment';

interface AppointmentsTabProps {
  clientId: string;
}

type StatusFilter = AppointmentStatus | 'all';
type DateRangeFilter = 'upcoming' | 'past' | 'all';

export function AppointmentsTab({ clientId }: AppointmentsTabProps) {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRangeFilter>('all');
  const [meetingTypeFilter, setMeetingTypeFilter] = useState<MeetingType | 'all'>('all');
  const [showOverdueAppointments, setShowOverdueAppointments] = useState(true);
  const [showPastAppointments, setShowPastAppointments] = useState(false);

  // Toast state with optional undo action
  const [toast, setToast] = useState<{
    message: string;
    action?: { label: string; onClick: () => void };
  } | null>(null);

  // Undo action manager
  const { scheduleAction, cleanup } = useUndoableAction(8000);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);

  // Cancel modal state
  const [cancelContext, setCancelContext] = useState<{
    appointment: Appointment | null;
  }>({ appointment: null });

  // Fetch appointments
  const appointmentsQuery = useClientAppointments(clientId);
  const appointments = appointmentsQuery.data ?? [];

  // Mutations
  const createMutation = useCreateAppointment(clientId);
  const updateMutation = useUpdateAppointment(
    clientId,
    editingAppointment?.id || ''
  );

  // Generic update mutation for any appointment
  const genericUpdateMutation = useMutation({
    mutationFn: async ({ appointmentId, payload }: { appointmentId: string; payload: any }) => {
      return apiRequest<Appointment>(
        `/api/clients/${clientId}/appointments/${appointmentId}`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        },
        session?.access_token,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments', clientId] });
    },
  });

  // Generic delete mutation for any appointment
  const genericDeleteMutation = useMutation({
    mutationFn: async (appointmentId: string) => {
      return apiRequest<{ success: boolean; message: string }>(
        `/api/clients/${clientId}/appointments/${appointmentId}`,
        {
          method: 'DELETE',
        },
        session?.access_token,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments', clientId] });
    },
  });

  // Cleanup scheduled actions on unmount
  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  // Filter and sort appointments
  const { upcomingAppointments, overdueAppointments, pastAppointments } = useMemo(() => {
    const now = new Date();
    let filtered = appointments;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((apt) => apt.status === statusFilter);
    }

    // Apply meeting type filter
    if (meetingTypeFilter !== 'all') {
      filtered = filtered.filter((apt) => apt.meeting_type === meetingTypeFilter);
    }

    // Split into upcoming, overdue, and past
    // Upcoming: scheduled appointments with future start time
    const upcoming = filtered
      .filter((apt) => apt.status === 'scheduled' && new Date(apt.starts_at) >= now)
      .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());

    // Overdue: scheduled appointments with past start time (not completed/cancelled)
    const overdue = filtered
      .filter((apt) => apt.status === 'scheduled' && new Date(apt.starts_at) < now)
      .sort((a, b) => new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime());

    // Past: appointments that are completed, cancelled, or no-show (regardless of time)
    const past = filtered
      .filter((apt) => apt.status === 'completed' || apt.status === 'cancelled' || apt.status === 'no_show')
      .sort((a, b) => new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime());

    return { upcomingAppointments: upcoming, overdueAppointments: overdue, pastAppointments: past };
  }, [appointments, statusFilter, meetingTypeFilter]);

  // Filter based on date range
  const displayedAppointments = useMemo(() => {
    if (dateRangeFilter === 'upcoming') {
      return [...upcomingAppointments, ...overdueAppointments];
    } else if (dateRangeFilter === 'past') {
      return pastAppointments;
    }
    // 'all' - show all three categories
    return [...upcomingAppointments, ...overdueAppointments, ...pastAppointments];
  }, [dateRangeFilter, upcomingAppointments, overdueAppointments, pastAppointments]);

  // Handlers
  const handleOpenForm = (appointment?: Appointment) => {
    setEditingAppointment(appointment || null);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingAppointment(null);
  };

  const combineDateTime = (date: string, time: string): string => {
    return new Date(`${date}T${time}`).toISOString();
  };

  const handleFormSubmit = async (formData: AppointmentFormData) => {
    try {
      const starts_at = combineDateTime(formData.date, formData.start_time);
      const ends_at = combineDateTime(formData.date, formData.end_time);

      if (editingAppointment) {
        // Update existing appointment
        await updateMutation.mutateAsync({
          title: formData.title,
          notes: formData.notes || undefined,
          starts_at,
          ends_at,
          meeting_type: formData.meeting_type,
          meeting_link: formData.meeting_link || undefined,
        });
        setToast({ message: 'Appointment updated successfully' });
      } else {
        // Create new appointment
        await createMutation.mutateAsync({
          title: formData.title,
          notes: formData.notes || undefined,
          starts_at,
          ends_at,
          meeting_type: formData.meeting_type,
          meeting_link: formData.meeting_link || undefined,
        });
        setToast({ message: 'Appointment created successfully' });
      }
      handleCloseForm();
    } catch (error) {
      console.error('Failed to save appointment:', error);
      setToast({ message: 'Failed to save appointment' });
    }
  };

  const handleStatusChange = (
    appointment: Appointment,
    status: 'completed' | 'cancelled' | 'no_show'
  ) => {
    if (status === 'cancelled') {
      // Open cancel modal (no undo needed - has its own confirmation)
      setCancelContext({ appointment });
      return;
    }

    // Store original status for rollback
    const originalStatus = appointment.status;

    // 1. Optimistic update: immediately update the UI
    queryClient.setQueryData<Appointment[]>(
      ['appointments', clientId],
      (old) => {
        if (!old) return old;
        return old.map((apt) =>
          apt.id === appointment.id ? { ...apt, status } : apt
        );
      }
    );

    // 2. Schedule the actual API call with undo capability
    const undoAction = scheduleAction(
      // Action: Execute the mutation after delay
      async () => {
        try {
          await genericUpdateMutation.mutateAsync({
            appointmentId: appointment.id,
            payload: { status },
          });
        } catch (error) {
          console.error('Failed to update appointment status:', error);
          setToast({ message: 'Failed to update appointment' });
          throw error; // Will trigger automatic rollback
        }
      },
      // Rollback: Revert UI to original status
      () => {
        queryClient.setQueryData<Appointment[]>(
          ['appointments', clientId],
          (old) => {
            if (!old) return old;
            return old.map((apt) =>
              apt.id === appointment.id ? { ...apt, status: originalStatus } : apt
            );
          }
        );
      }
    );

    // 3. Show toast with undo button
    const statusMessage =
      status === 'completed'
        ? 'Appointment marked as completed'
        : 'Appointment marked as no-show';

    setToast({
      message: statusMessage,
      action: {
        label: 'Undo',
        onClick: undoAction,
      },
    });
  };

  const handleCancelAppointment = async (reason: string) => {
    if (!cancelContext.appointment) return;

    try {
      await genericUpdateMutation.mutateAsync({
        appointmentId: cancelContext.appointment.id,
        payload: {
          status: 'cancelled',
          cancellation_reason: reason,
        },
      });
      setToast({ message: 'Appointment cancelled' });
      setCancelContext({ appointment: null });
    } catch (error) {
      console.error('Failed to cancel appointment:', error);
      setToast({ message: 'Failed to cancel appointment' });
    }
  };

  const handleDeleteAppointment = async (appointment: Appointment) => {
    if (!confirm(`Are you sure you want to delete "${appointment.title}"?`)) {
      return;
    }

    try {
      await genericDeleteMutation.mutateAsync(appointment.id);
      setToast({ message: 'Appointment deleted' });
    } catch (error) {
      console.error('Failed to delete appointment:', error);
      setToast({ message: 'Failed to delete appointment' });
    }
  };

  if (appointmentsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading appointments...</div>
      </div>
    );
  }

  if (appointmentsQuery.isError) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-500">Failed to load appointments</div>
      </div>
    );
  }

  const isEmpty = appointments.length === 0;
  const noFilteredResults = displayedAppointments.length === 0 && !isEmpty;

  return (
    <div className="flex-1 overflow-y-auto pb-24">
      {toast && (
        <Toast
          message={toast.message}
          onClose={() => setToast(null)}
          duration={toast.action ? 8000 : 3000}
          action={toast.action}
        />
      )}

      {/* Filter Section */}
      <div className="p-4 space-y-3 border-b border-gray-200 bg-white sticky top-0 z-10">
        {/* Status Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {(['all', 'scheduled', 'completed', 'cancelled', 'no_show'] as const).map((filter) => (
            <button
              key={filter}
              onClick={() => setStatusFilter(filter)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                statusFilter === filter
                  ? 'bg-[#C9A870] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter === 'all' && 'All'}
              {filter === 'scheduled' && 'Scheduled'}
              {filter === 'completed' && 'Completed'}
              {filter === 'cancelled' && 'Cancelled'}
              {filter === 'no_show' && 'No Show'}
            </button>
          ))}
        </div>

        {/* Date Range & Meeting Type Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {/* Date Range */}
          {(['all', 'upcoming', 'past'] as const).map((filter) => (
            <button
              key={filter}
              onClick={() => setDateRangeFilter(filter)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                dateRangeFilter === filter
                  ? 'bg-[#5B8E87] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter === 'all' && 'All Time'}
              {filter === 'upcoming' && 'Upcoming'}
              {filter === 'past' && 'Past'}
            </button>
          ))}

          {/* Meeting Type */}
          <div className="border-l border-gray-300 pl-2 ml-1" />
          {(['all', 'in_person', 'video', 'phone'] as const).map((filter) => (
            <button
              key={filter}
              onClick={() => setMeetingTypeFilter(filter)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                meetingTypeFilter === filter
                  ? 'bg-[#5B8E87] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter === 'all' && 'All Types'}
              {filter === 'in_person' && <><MapPinIcon className="w-4 h-4" /><span>In-Person</span></>}
              {filter === 'video' && <><VideoIcon className="w-4 h-4" /><span>Video</span></>}
              {filter === 'phone' && <><PhoneIcon className="w-4 h-4" /><span>Phone</span></>}
            </button>
          ))}
        </div>
      </div>

      {/* Empty State */}
      {isEmpty && (
        <div className="p-4">
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center border border-gray-100">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CalendarIcon className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-[#212121] mb-2">
              No appointments yet
            </h3>
            <p className="text-gray-600 mb-4">
              Schedule your first consultation with this client
            </p>
            <button
              onClick={() => handleOpenForm()}
              className="bg-[#5B8E87] text-white px-6 py-2 rounded-lg font-medium"
            >
              Add appointment
            </button>
          </div>
        </div>
      )}

      {/* No Results After Filtering */}
      {noFilteredResults && (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <CalendarIcon className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No appointments found
          </h3>
          <p className="text-sm text-gray-500 text-center">
            Try adjusting your filters
          </p>
        </div>
      )}

      {/* Appointments List */}
      {!isEmpty && !noFilteredResults && (
        <div className="p-4 space-y-6">
          {/* Upcoming Appointments */}
          {dateRangeFilter !== 'past' && upcomingAppointments.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Upcoming Appointments ({upcomingAppointments.length})
              </h3>
              <div className="space-y-3">
                {upcomingAppointments.map((appointment) => (
                  <AppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                    clientId={clientId}
                    onEdit={handleOpenForm}
                    onDelete={handleDeleteAppointment}
                    onStatusChange={handleStatusChange}
                    onWhatsAppSuccess={(message) => setToast({ message })}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Overdue Appointments (Collapsible) */}
          {dateRangeFilter !== 'past' && overdueAppointments.length > 0 && (
            <div>
              <button
                onClick={() => setShowOverdueAppointments(!showOverdueAppointments)}
                className="w-full flex items-center justify-between text-sm font-semibold text-red-600 uppercase tracking-wide mb-3 hover:text-red-700 transition-colors"
              >
                <span>Overdue Appointments ({overdueAppointments.length})</span>
                {showOverdueAppointments ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </button>
              {showOverdueAppointments && (
                <div className="space-y-3">
                  {overdueAppointments.map((appointment) => (
                    <AppointmentCard
                      key={appointment.id}
                      appointment={appointment}
                      clientId={clientId}
                      onEdit={handleOpenForm}
                      onDelete={handleDeleteAppointment}
                      onStatusChange={handleStatusChange}
                      onWhatsAppSuccess={(message) => setToast({ message })}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Past Appointments (Collapsible) */}
          {dateRangeFilter !== 'upcoming' && pastAppointments.length > 0 && (
            <div>
              <button
                onClick={() => setShowPastAppointments(!showPastAppointments)}
                className="w-full flex items-center justify-between text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 hover:text-gray-700 transition-colors"
              >
                <span>Past Appointments ({pastAppointments.length})</span>
                {showPastAppointments ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </button>
              {showPastAppointments && (
                <div className="space-y-3">
                  {pastAppointments.map((appointment) => (
                    <AppointmentCard
                      key={appointment.id}
                      appointment={appointment}
                      clientId={clientId}
                      onEdit={handleOpenForm}
                      onDelete={handleDeleteAppointment}
                      onStatusChange={handleStatusChange}
                      onWhatsAppSuccess={(message) => setToast({ message })}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Floating Action Button */}
      <button
        onClick={() => handleOpenForm()}
        className="fixed bottom-24 right-6 bg-[#C9A870] text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-[#B89660] transition-all active:scale-95 z-20"
      >
        <PlusIcon className="w-6 h-6" />
      </button>

      {/* Appointment Form Modal */}
      <AppointmentFormModal
        isOpen={showForm}
        onClose={handleCloseForm}
        onSubmit={handleFormSubmit}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        editingAppointment={editingAppointment}
        clientId={clientId}
      />

      {/* Cancel Appointment Modal */}
      <CancelAppointmentModal
        isOpen={!!cancelContext.appointment}
        onClose={() => setCancelContext({ appointment: null })}
        onConfirm={handleCancelAppointment}
        isSubmitting={genericUpdateMutation.isPending}
        appointmentTitle={cancelContext.appointment?.title || ''}
      />
    </div>
  );
}
